import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import { AppShell } from '@/components/common/AppShell';
import { PlaceholderPage } from '@/components/common/PlaceholderPage';
import { RequireAuth } from '@/shared/auth/RequireAuth';

// Public landing
const LandingLayout = lazy(() => import('@/features/landing/LandingLayout'));
const HomePage = lazy(() => import('@/features/landing/pages/HomePage'));
const LandingFeaturesPage = lazy(() => import('@/features/landing/pages/FeaturesPage'));
const HowItWorksPage = lazy(() => import('@/features/landing/pages/HowItWorksPage'));
const LandingCustomersPage = lazy(() => import('@/features/landing/pages/CustomersPage'));
const FaqPage = lazy(() => import('@/features/landing/pages/FaqPage'));
const SolutionsPage = lazy(() => import('@/features/landing/pages/SolutionsPage'));

// Auth & system
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'));
const NotFound = lazy(() => import('@/features/system/pages/NotFound'));
const Forbidden = lazy(() => import('@/features/system/pages/Forbidden'));

// Main
const Dashboard = lazy(() => import('@/features/dashboard/pages/Dashboard'));
const POSPage = lazy(() => import('@/features/pos/pages/POSPage'));
const CustomerDisplay = lazy(() => import('@/features/customer-display/pages/CustomerDisplay'));

// User Management
const UsersList = lazy(() => import('@/features/users/pages/UsersList'));
const UserForm = lazy(() => import('@/features/users/pages/UserForm'));
const RolesList = lazy(() => import('@/features/roles/pages/RolesList'));
const RoleForm = lazy(() => import('@/features/roles/pages/RoleForm'));

// Master data
const CurrenciesList = lazy(() => import('@/features/currencies/pages/CurrenciesList'));
const WarehousesPage = lazy(() => import('@/features/warehouses'));
const CategoriesPage = lazy(() => import('@/features/categories'));
const SubCategoriesPage = lazy(() => import('@/features/subcategories'));
const BrandsPage = lazy(() => import('@/features/brands'));
const UnitsPage = lazy(() => import('@/features/units'));
const PaymentMethodsPage = lazy(() => import('@/features/payment-methods'));

// People
const CustomersPage = lazy(() => import('@/features/customers'));
const CustomerForm = lazy(() => import('@/features/customers/pages/CustomerForm'));
const CustomersImport = lazy(() => import('@/features/customers/pages/CustomersImport'));
const CustomersEcommerce = lazy(() => import('@/features/customers/pages/CustomersEcommerce'));
const CustomersWithoutEcommerce = lazy(
  () => import('@/features/customers/pages/CustomersWithoutEcommerce'),
);
const CustomerLedger = lazy(() => import('@/features/customers/pages/CustomerLedger'));
const CustomerDetails = lazy(() => import('@/features/customers/pages/CustomerDetails'));
const SuppliersPage = lazy(() => import('@/features/suppliers'));
const SuppliersImport = lazy(() => import('@/features/suppliers/pages/SuppliersImport'));

// Products
const ProductsList = lazy(() => import('@/features/products/pages/ProductsList'));
const ProductForm = lazy(() => import('@/features/products/pages/ProductForm'));
const ImportProducts = lazy(() => import('@/features/products/pages/ImportProducts'));
const OpeningStock = lazy(() => import('@/features/products/pages/OpeningStock'));
const PrintLabels = lazy(() => import('@/features/products/pages/PrintLabels'));
const CountStock = lazy(() => import('@/features/products/pages/CountStock'));
const ImportProductsUpdateOnly = lazy(async () => {
  const m = await import('@/features/products/pages/ImportProducts');
  return { default: m.ImportProductsUpdateOnly };
});

// Sales
const SalesList = lazy(() => import('@/features/sales/pages/SalesList'));
const SaleForm = lazy(() => import('@/features/sales/pages/SaleForm'));
const ImportSales = lazy(() => import('@/features/sales/pages/ImportSales'));
const Shipments = lazy(() => import('@/features/sales/pages/Shipments'));

// Purchases
const PurchasesList = lazy(() => import('@/features/purchases/pages/PurchasesList'));
const PurchaseForm = lazy(() => import('@/features/purchases/pages/PurchaseForm'));
const ImportPurchases = lazy(() => import('@/features/purchases/pages/ImportPurchases'));

// Inventory ops
const AdjustmentsPage = lazy(() => import('@/features/inventory/AdjustmentsPage'));
const AdjustmentForm = lazy(() => import('@/features/inventory/AdjustmentForm'));
const DamagesPage = lazy(() => import('@/features/inventory/DamagesPage'));
const DamageForm = lazy(() => import('@/features/inventory/DamageForm'));
const TransfersPage = lazy(() => import('@/features/inventory/TransfersPage'));
const TransferForm = lazy(() => import('@/features/inventory/TransferForm'));

// Quotations & Returns
const QuotationsList = lazy(() => import('@/features/quotations/QuotationsList'));
const QuotationForm = lazy(() => import('@/features/quotations/QuotationForm'));
const SaleReturnsList = lazy(() => import('@/features/sale-returns/SaleReturnsList'));
const PurchaseReturnsList = lazy(() => import('@/features/purchase-returns/PurchaseReturnsList'));

// Accounting
const ExpensesPage = lazy(() => import('@/features/expenses'));
const DepositsPage = lazy(() => import('@/features/deposits'));
const AccountingDashboard = lazy(() => import('@/features/accounting/dashboard/AccountingDashboard'));
const ChartOfAccountsPage = lazy(() => import('@/features/accounting/chart-of-accounts'));
const JournalEntriesPage = lazy(() => import('@/features/accounting/journal-entries'));
const JournalEntryForm = lazy(() => import('@/features/accounting/journal-entries/JournalEntryForm'));
const TransferMoneyPage = lazy(() => import('@/features/accounting/transfer-money'));
const AccountsListPage = lazy(() => import('@/features/accounts'));
const TrialBalancePage = lazy(() => import('@/features/accounting/reports/TrialBalance'));
const BalanceSheetPage = lazy(() => import('@/features/accounting/reports/BalanceSheet'));
const TaxReportV2Page = lazy(() => import('@/features/accounting/reports/TaxReport'));
const ProfitLossV2Page = lazy(() => import('@/features/accounting/reports/ProfitLossV2'));
const ExpenseCategoriesPage = lazy(() => import('@/features/expenses/Categories'));
const DepositCategoriesPage = lazy(() => import('@/features/deposits/Categories'));
const AccountFormPage = lazy(() => import('@/features/accounts/AccountForm'));
const ExpenseFormPage = lazy(() => import('@/features/expenses/ExpenseForm'));
const ExpenseCategoryFormPage = lazy(() => import('@/features/expenses/CategoryForm'));
const DepositFormPage = lazy(() => import('@/features/deposits/DepositForm'));
const DepositCategoryFormPage = lazy(() => import('@/features/deposits/CategoryForm'));
const TransferMoneyFormPage = lazy(() => import('@/features/accounting/transfer-money/TransferMoneyForm'));


// Online Store
const OrdersList = lazy(() => import('@/features/online-store/OrdersList'));
const OrderShow = lazy(() => import('@/features/online-store/OrderShow'));
const StorePagesPage = lazy(() => import('@/features/online-store/PagesList'));
const BannersList = lazy(() => import('@/features/online-store/BannersList'));
const CollectionsList = lazy(() => import('@/features/online-store/CollectionsList'));
const CollectionForm = lazy(() => import('@/features/online-store/CollectionForm'));
const SubscribersList = lazy(() => import('@/features/online-store/SubscribersList'));
const MessagesList = lazy(() => import('@/features/online-store/MessagesList'));
const StoreSettings = lazy(() => import('@/features/online-store/StoreSettings'));

// Integrations
const WooCommercePage = lazy(async () => ({
  default: (await import('@/features/settings/pages/IntegrationsPlaceholder')).WooCommercePage,
}));
const QuickBooksPage = lazy(async () => ({
  default: (await import('@/features/settings/pages/IntegrationsPlaceholder')).QuickBooksPage,
}));

// Reports + Settings hubs
const ReportsIndex = lazy(() => import('@/features/reports/pages/ReportsIndex'));
const SystemSettings = lazy(() => import('@/features/settings/pages/SystemSettings'));
const AppearanceSettings = lazy(() => import('@/features/settings/pages/AppearanceSettings'));
const PosSettings = lazy(() => import('@/features/settings/pages/PosSettings'));
const MailSettings = lazy(() => import('@/features/settings/pages/MailSettings'));
const SmsSettings = lazy(() => import('@/features/settings/pages/SmsSettings'));
const BackupSettings = lazy(() => import('@/features/settings/pages/BackupSettings'));
const ModulesSettings = lazy(() => import('@/features/settings/pages/ModulesSettings'));
const ErrorLogs = lazy(() => import('@/features/settings/pages/ErrorLogs'));
const LoginDevices = lazy(() => import('@/features/settings/pages/LoginDevices'));
const SmsTemplates = lazy(() => import('@/features/settings/pages/SmsTemplates'));
const TranslationsSettings = lazy(() => import('@/features/settings/pages/TranslationsSettings'));
const TranslationsView = lazy(() => import('@/features/settings/pages/TranslationsView'));
const QuickBooksSync = lazy(() => import('@/features/settings/pages/QuickBooksSync'));
const EmailTemplates = lazy(() => import('@/features/settings/pages/EmailTemplates'));
const UpdateSettings = lazy(() => import('@/features/settings/pages/UpdateSettings'));
const PaymentGateway = lazy(() => import('@/features/settings/pages/PaymentGateway'));
const PosReceipt = lazy(() => import('@/features/settings/pages/PosReceipt'));

// Implemented reports (lazy-loaded as a single chunk via the configs file)
const SalesReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).SalesReport,
}));
const PurchasesReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).PurchasesReport,
}));
const TopProductsReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).TopProductsReport,
}));
const TopCustomersReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).TopCustomersReport,
}));
const StockReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).StockReport,
}));
const CustomersReportPage = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).CustomersReport,
}));
const SuppliersReportPage = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).SuppliersReport,
}));
const ProfitLossReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).ProfitLossReport,
}));
const QuantityAlertsReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).QuantityAlertsReport,
}));
const WarehouseReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).WarehouseReport,
}));
const PaymentsSalesReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).PaymentsSalesReport,
}));
const PaymentsPurchasesReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).PaymentsPurchasesReport,
}));
const PaymentsSaleReturnsReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).PaymentsSaleReturnsReport,
}));
const PaymentsPurchaseReturnsReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).PaymentsPurchaseReturnsReport,
}));

// Long-tail reports
const InventoryValuationReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).InventoryValuationReport,
}));
const DeadStockReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).DeadStockReport,
}));
const ZeroSalesReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).ZeroSalesReport,
}));
const NegativeStockReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).NegativeStockReport,
}));
const StockAgingReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).StockAgingReport,
}));
const InactiveCustomersReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).InactiveCustomersReport,
}));
const LoyaltyPointsReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).LoyaltyPointsReport,
}));
const CashFlowReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).CashFlowReport,
}));
const TaxSummaryReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).TaxSummaryReport,
}));
const ExpensesReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).ExpensesReport,
}));
const DepositsReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).DepositsReport,
}));
const CashRegisterReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).CashRegisterReport,
}));
const UsersReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).UsersReport,
}));
const AttendanceReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).AttendanceReport,
}));
const ServiceJobsReportPage = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).ServiceJobsReportPage,
}));
const DiscountSummaryReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).DiscountSummaryReport,
}));
const ProductSalesReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).ProductSalesReport,
}));
const ProductPurchasesReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).ProductPurchasesReport,
}));
const SellerReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).SellerReport,
}));
const ProductReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).ProductReport,
}));
const TransactionsReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).TransactionsReport,
}));
const SalesByCategoryReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).SalesByCategoryReport,
}));
const SalesByBrandReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).SalesByBrandReport,
}));
const DraftInvoicesReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).DraftInvoicesReport,
}));
const StockInventoryValuationReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).StockInventoryValuationReport,
}));
const StockTransferReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).StockTransferReport,
}));
const StockAdjustmentReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).StockAdjustmentReport,
}));
const ReturnRatioReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).ReturnRatioReport,
}));
const WarrantyGuaranteeReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).WarrantyGuaranteeReport,
}));
const CustomerMaintenanceHistoryReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).CustomerMaintenanceHistoryReport,
}));
const ServiceChecklistReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).ServiceChecklistReport,
}));
const AnalyticsReport = lazy(async () => ({
  default: (await import('@/features/reports/pages/GenericReportPage')).AnalyticsReport,
}));
const LoginActivityReport = lazy(() => import('@/features/reports/pages/LoginActivityReport'));
const AIReport = lazy(() => import('@/features/reports/pages/AIReport'));

const stub = (title: string, description?: string) =>
  description ? (
    <PlaceholderPage title={title} description={description} />
  ) : (
    <PlaceholderPage title={title} />
  );

export const router = createBrowserRouter([
  // Public marketing pages (no auth required)
  {
    element: <LandingLayout />,
    children: [
      { path: '/', element: <HomePage /> },
      { path: '/features', element: <LandingFeaturesPage /> },
      { path: '/how-it-works', element: <HowItWorksPage /> },
      { path: '/customers', element: <LandingCustomersPage /> },
      { path: '/faq', element: <FaqPage /> },
      { path: '/solutions', element: <SolutionsPage /> },
      // Legacy pricing redirect (page removed)
      { path: '/pricing', element: <Navigate to="/" replace /> },
    ],
  },
  { path: '/login', element: <LoginPage /> },
  { path: '/403', element: <Forbidden /> },

  // Full-screen routes (outside the AppShell)
  {
    element: <RequireAuth />,
    children: [
      { path: '/app/pos', element: <POSPage /> },
      { path: '/app/customer-display', element: <CustomerDisplay /> },
    ],
  },

  // Standard app shell
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: '/app', element: <Navigate to="/app/dashboard" replace /> },
          { path: '/app/dashboard', element: <Dashboard /> },

          // User Management
          { path: '/app/users', element: <UsersList /> },
          { path: '/app/users/new', element: <UserForm /> },
          { path: '/app/users/:id/edit', element: <UserForm /> },
          { path: '/app/roles', element: <RolesList /> },
          { path: '/app/roles/new', element: <RoleForm /> },
          { path: '/app/roles/:id/edit', element: <RoleForm /> },

          // Products
          { path: '/app/products', element: <ProductsList /> },
          { path: '/app/products/new', element: <ProductForm /> },
          { path: '/app/products/:id/edit', element: <ProductForm /> },
          { path: '/app/products/import', element: <ImportProducts /> },
          { path: '/app/products/import-update', element: <ImportProductsUpdateOnly /> },
          { path: '/app/products/opening-stock', element: <OpeningStock /> },
          { path: '/app/products/print-labels', element: <PrintLabels /> },
          { path: '/app/count-stock', element: <CountStock /> },
          { path: '/app/categories', element: <CategoriesPage /> },
          { path: '/app/subcategories', element: <SubCategoriesPage /> },
          { path: '/app/brands', element: <BrandsPage /> },
          { path: '/app/units', element: <UnitsPage /> },

          // Sales
          { path: '/app/sales', element: <SalesList /> },
          { path: '/app/sales/new', element: <SaleForm /> },
          { path: '/app/sales/:id/edit', element: <SaleForm /> },
          { path: '/app/sales/import', element: <ImportSales /> },
          { path: '/app/quotations', element: <QuotationsList /> },
          { path: '/app/quotations/new', element: <QuotationForm /> },
          { path: '/app/quotations/:id/edit', element: <QuotationForm /> },
          { path: '/app/sale-returns', element: <SaleReturnsList /> },
          { path: '/app/shipments', element: <Shipments /> },

          // Purchases
          { path: '/app/purchases', element: <PurchasesList /> },
          { path: '/app/purchases/new', element: <PurchaseForm /> },
          { path: '/app/purchases/:id/edit', element: <PurchaseForm /> },
          { path: '/app/purchases/import', element: <ImportPurchases /> },
          { path: '/app/purchase-returns', element: <PurchaseReturnsList /> },

          // Inventory
          { path: '/app/adjustments', element: <AdjustmentsPage /> },
          { path: '/app/adjustments/new', element: <AdjustmentForm /> },
          { path: '/app/adjustments/:id/edit', element: <AdjustmentForm /> },
          { path: '/app/damages', element: <DamagesPage /> },
          { path: '/app/damages/new', element: <DamageForm /> },
          { path: '/app/damages/:id/edit', element: <DamageForm /> },
          { path: '/app/transfers', element: <TransfersPage /> },
          { path: '/app/transfers/new', element: <TransferForm /> },
          { path: '/app/transfers/:id/edit', element: <TransferForm /> },
          { path: '/app/warehouses', element: <WarehousesPage /> },

          // People
          { path: '/app/customers', element: <CustomersPage /> },
          { path: '/app/customers/new', element: <CustomerForm /> },
          { path: '/app/customers/:id/edit', element: <CustomerForm /> },
          { path: '/app/customers/:id/ledger', element: <CustomerLedger /> },
          { path: '/app/customers/:id/details', element: <CustomerDetails /> },
          { path: '/app/customers/import', element: <CustomersImport /> },
          { path: '/app/customers/ecommerce', element: <CustomersEcommerce /> },
          {
            path: '/app/customers/without-ecommerce',
            element: <CustomersWithoutEcommerce />,
          },
          { path: '/app/suppliers', element: <SuppliersPage /> },
          { path: '/app/suppliers/import', element: <SuppliersImport /> },

          // Accounting
          { path: '/app/accounting/dashboard', element: <AccountingDashboard /> },
          { path: '/app/accounting/chart-of-accounts', element: <ChartOfAccountsPage /> },
          { path: '/app/accounting/journal-entries', element: <JournalEntriesPage /> },
          { path: '/app/accounting/journal-entries/new', element: <JournalEntryForm /> },
          { path: '/app/accounting/journal-entries/:id/edit', element: <JournalEntryForm /> },
          { path: '/app/accounting/reports/trial-balance', element: <TrialBalancePage /> },
          { path: '/app/accounting/reports/profit-loss', element: <ProfitLossV2Page /> },
          { path: '/app/accounting/reports/balance-sheet', element: <BalanceSheetPage /> },
          { path: '/app/accounting/reports/tax-summary', element: <TaxReportV2Page /> },
          { path: '/app/accounts', element: <AccountsListPage /> },
          { path: '/app/accounts/new', element: <AccountFormPage /> },
          { path: '/app/accounts/:id/edit', element: <AccountFormPage /> },
          { path: '/app/expenses', element: <ExpensesPage /> },
          { path: '/app/expenses/new', element: <ExpenseFormPage /> },
          { path: '/app/expenses/:id/edit', element: <ExpenseFormPage /> },
          { path: '/app/expenses/categories', element: <ExpenseCategoriesPage /> },
          { path: '/app/expenses/categories/new', element: <ExpenseCategoryFormPage /> },
          { path: '/app/expenses/categories/:id/edit', element: <ExpenseCategoryFormPage /> },
          { path: '/app/deposits', element: <DepositsPage /> },
          { path: '/app/deposits/new', element: <DepositFormPage /> },
          { path: '/app/deposits/:id/edit', element: <DepositFormPage /> },
          { path: '/app/deposits/categories', element: <DepositCategoriesPage /> },
          { path: '/app/deposits/categories/new', element: <DepositCategoryFormPage /> },
          { path: '/app/deposits/categories/:id/edit', element: <DepositCategoryFormPage /> },
          { path: '/app/transfer-money', element: <TransferMoneyPage /> },
          { path: '/app/transfer-money/new', element: <TransferMoneyFormPage /> },
          { path: '/app/transfer-money/:id/edit', element: <TransferMoneyFormPage /> },

          // Online Store
          { path: '/app/store/orders', element: <OrdersList /> },
          { path: '/app/store/orders/:id', element: <OrderShow /> },
          { path: '/app/store/pages', element: <StorePagesPage /> },
          { path: '/app/store/banners', element: <BannersList /> },
          { path: '/app/store/collections', element: <CollectionsList /> },
          { path: '/app/store/collections/create', element: <CollectionForm /> },
          { path: '/app/store/collections/:id/edit', element: <CollectionForm /> },
          { path: '/app/store/subscribers', element: <SubscribersList /> },
          { path: '/app/store/messages', element: <MessagesList /> },
          { path: '/app/store/settings', element: <StoreSettings /> },

          // Integrations
          { path: '/app/woocommerce', element: <WooCommercePage /> },
          { path: '/app/quickbooks', element: <QuickBooksPage /> },

          // Reports
          { path: '/app/reports', element: <ReportsIndex /> },
          { path: '/app/reports/sales', element: <SalesReport /> },
          { path: '/app/reports/purchases', element: <PurchasesReport /> },
          { path: '/app/reports/top-selling-products', element: <TopProductsReport /> },
          { path: '/app/reports/top-customers', element: <TopCustomersReport /> },
          { path: '/app/reports/top-suppliers', element: <SuppliersReportPage /> },
          { path: '/app/reports/stock', element: <StockReport /> },
          { path: '/app/reports/customers', element: <CustomersReportPage /> },
          { path: '/app/reports/profit-and-loss', element: <ProfitLossReport /> },
          { path: '/app/reports/quantity-alerts', element: <QuantityAlertsReport /> },
          { path: '/app/reports/warehouse', element: <WarehouseReport /> },
          { path: '/app/reports/payments-sale', element: <PaymentsSalesReport /> },
          { path: '/app/reports/payments-purchase', element: <PaymentsPurchasesReport /> },
          { path: '/app/reports/payments-sales-returns', element: <PaymentsSaleReturnsReport /> },
          { path: '/app/reports/payments-purchase-returns', element: <PaymentsPurchaseReturnsReport /> },
          // Long-tail reports — wired with the same ReportPage framework
          { path: '/app/reports/inventory-valuation', element: <InventoryValuationReport /> },
          { path: '/app/reports/stock-aging', element: <StockAgingReport /> },
          { path: '/app/reports/dead-stock', element: <DeadStockReport /> },
          { path: '/app/reports/zero-sales', element: <ZeroSalesReport /> },
          { path: '/app/reports/negative-stock', element: <NegativeStockReport /> },
          { path: '/app/reports/stock-adjustment', element: <StockAdjustmentReport /> },
          { path: '/app/reports/inactive-customers', element: <InactiveCustomersReport /> },
          { path: '/app/reports/loyalty-points', element: <LoyaltyPointsReport /> },
          { path: '/app/reports/cash-flow', element: <CashFlowReport /> },
          { path: '/app/reports/tax-summary', element: <TaxSummaryReport /> },
          { path: '/app/reports/expenses', element: <ExpensesReport /> },
          { path: '/app/reports/deposits', element: <DepositsReport /> },
          { path: '/app/reports/cash-register', element: <CashRegisterReport /> },
          { path: '/app/reports/discount-summary', element: <DiscountSummaryReport /> },
          { path: '/app/reports/return-ratio', element: <ReturnRatioReport /> },
          { path: '/app/reports/product-purchases', element: <ProductPurchasesReport /> },
          { path: '/app/reports/users', element: <UsersReport /> },
          { path: '/app/reports/login-activity', element: <LoginActivityReport /> },
          { path: '/app/reports/error-logs', element: <ErrorLogs /> },
          { path: '/app/reports/analytics', element: <AnalyticsReport /> },
          { path: '/app/reports/ai', element: <AIReport /> },
          { path: '/app/reports/attendance', element: <AttendanceReport /> },
          { path: '/app/reports/warranty-guarantee', element: <WarrantyGuaranteeReport /> },
          { path: '/app/reports/service-jobs', element: <ServiceJobsReportPage /> },
          { path: '/app/reports/service-checklist', element: <ServiceChecklistReport /> },
          { path: '/app/reports/customer-maintenance-history', element: <CustomerMaintenanceHistoryReport /> },
          // Reports added to match the legacy sidebar (still stubs until backend pages are migrated)
          { path: '/app/reports/product-sales', element: <ProductSalesReport /> },
          { path: '/app/reports/seller', element: <SellerReport /> },
          { path: '/app/reports/draft-invoices', element: <DraftInvoicesReport /> },
          { path: '/app/reports/sales-by-category', element: <SalesByCategoryReport /> },
          { path: '/app/reports/sales-by-brand', element: <SalesByBrandReport /> },
          { path: '/app/reports/suppliers', element: <SuppliersReportPage /> },
          { path: '/app/reports/stock-inventory-valuation', element: <StockInventoryValuationReport /> },
          { path: '/app/reports/stock-transfer', element: <StockTransferReport /> },
          { path: '/app/reports/product', element: <ProductReport /> },
          { path: '/app/reports/transactions', element: <TransactionsReport /> },

          // Settings
          { path: '/app/settings', element: <Navigate to="/app/settings/system" replace /> },
          { path: '/app/settings/system', element: <SystemSettings /> },
          { path: '/app/settings/appearance', element: <AppearanceSettings /> },
          { path: '/app/settings/appearance_settings', element: <AppearanceSettings /> },
          { path: '/app/settings/languages', element: stub('settings.languages') },
          { path: '/app/settings/translations', element: <TranslationsSettings /> },
          { path: '/app/settings/translations_settings', element: <TranslationsSettings /> },
          { path: '/app/settings/translations/:locale', element: <TranslationsView /> },
          { path: '/app/settings/currencies', element: <CurrenciesList /> },
          { path: '/app/settings/custom-fields', element: stub('settings.custom_fields') },
          { path: '/app/settings/pos', element: <PosSettings /> },
          { path: '/app/settings/pos_settings', element: <PosSettings /> },
          { path: '/app/settings/pos-receipt', element: <PosReceipt /> },
          { path: '/app/settings/pos_receipt', element: <PosReceipt /> },
          { path: '/app/settings/payment-methods', element: <PaymentMethodsPage /> },
          { path: '/app/settings/payment_methods', element: <PaymentMethodsPage /> },
          { path: '/app/settings/payment-gateway', element: <PaymentGateway /> },
          { path: '/app/settings/payment_gateway', element: <PaymentGateway /> },
          { path: '/app/settings/mail', element: <MailSettings /> },
          { path: '/app/settings/mail_settings', element: <MailSettings /> },
          { path: '/app/settings/email-templates', element: <EmailTemplates /> },
          { path: '/app/settings/email_templates', element: <EmailTemplates /> },
          { path: '/app/settings/sms', element: <SmsSettings /> },
          { path: '/app/settings/sms_settings', element: <SmsSettings /> },
          { path: '/app/settings/sms-templates', element: <SmsTemplates /> },
          { path: '/app/settings/sms_templates', element: <SmsTemplates /> },
          { path: '/app/settings/quickbooks_sync', element: <QuickBooksSync /> },
          { path: '/app/settings/security', element: stub('settings.security') },
          { path: '/app/settings/login-devices', element: <LoginDevices /> },
          { path: '/app/settings/login_devices', element: <LoginDevices /> },
          { path: '/app/settings/backup', element: <BackupSettings /> },
          { path: '/app/settings/Backup', element: <BackupSettings /> },
          { path: '/app/settings/Warehouses', element: <WarehousesPage /> },
          { path: '/app/settings/warehouses', element: <WarehousesPage /> },
          { path: '/app/settings/Currencies', element: <CurrenciesList /> },
          { path: '/app/settings/update', element: <UpdateSettings /> },
          { path: '/app/settings/update_settings', element: <UpdateSettings /> },
          { path: '/app/settings/modules', element: <ModulesSettings /> },
          { path: '/app/settings/module_settings', element: <ModulesSettings /> },
          { path: '/app/settings/error-logs', element: <ErrorLogs /> },
          { path: '/app/settings/notifications', element: stub('settings.notifications') },
          { path: '/app/settings/google-calendar', element: stub('settings.google_calendar') },
          { path: '/app/settings/api-tokens', element: stub('settings.api_tokens') },

          { path: '/app/profile', element: stub('common.profile') },

          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },
]);
